/**
 * CursorGlow — Radial gradient follows cursor across all pages.
 *
 * Fixed overlay, pointer-events: none, z-index: 0 (behind content).
 * Spring-smoothed position via useMotionValue + useSpring.
 * No-op on touch devices and prefers-reduced-motion.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'motion/react';

const isCoarse =
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: coarse)').matches;

const prefersReduced =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SIZE = 600;
const SPRING = { stiffness: 80, damping: 20 };

export function CursorGlow() {
  const disabled = useRef(isCoarse || prefersReduced);
  const [visible, setVisible] = useState(false);

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const x = useSpring(rawX, SPRING);
  const y = useSpring(rawY, SPRING);

  useEffect(() => {
    if (disabled.current) return;

    const onMove = (e: MouseEvent) => {
      rawX.set(e.clientX - SIZE / 2);
      rawY.set(e.clientY - SIZE / 2);
      setVisible(true);
    };

    const onLeave = () => setVisible(false);

    window.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, [rawX, rawY]);

  if (disabled.current) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}
      aria-hidden="true"
    >
      <motion.div
        style={{
          x,
          y,
          width: SIZE,
          height: SIZE,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, color-mix(in srgb, var(--color-primary-500) 8%, transparent) 0%, transparent 70%)',
          opacity: visible ? 1 : 0,
          willChange: 'transform',
          transition: 'opacity 0.3s',
        }}
      />
    </div>
  );
}
